import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
  IconButton,
  LinearProgress,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  TrendingUp,
  TrendingDown,
  Inventory,
  ShoppingCart,
  People,
  Warning,
  AttachMoney,
  LocalShipping,
  Refresh,
  MoreVert,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { dashboardService } from '../../../services';
import { useAuth } from '../../../context/AuthContext';

// Styled Components
const WelcomeCard = styled(Card)({
  background: 'linear-gradient(135deg, #2e5a33 0%, #4a7c59 50%, #3d7342 100%)',
  color: 'white',
  borderRadius: '24px',
  boxShadow: '0 10px 40px rgba(46, 90, 51, 0.3)',
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '300px',
    height: '300px',
    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
    transform: 'translate(30%, -30%)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '200px',
    height: '200px',
    background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
    transform: 'translate(-30%, 30%)',
  },
});

const StatsCard = styled(Card)(({ theme }) => ({
  borderRadius: '20px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: 'white',
  border: '1px solid rgba(46, 90, 51, 0.08)',
  overflow: 'visible',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(46, 90, 51, 0.15)',
  },
}));

const StatsIconWrapper = styled(Box)<{ bgcolor: string }>(({ bgcolor }) => ({
  width: '60px',
  height: '60px',
  borderRadius: '16px',
  background: `linear-gradient(135deg, ${bgcolor} 0%, ${bgcolor}dd 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  boxShadow: `0 8px 24px ${bgcolor}40`,
  position: 'relative',
  top: '-30px',
  marginBottom: '-15px',
}));

const QuickActionCard = styled(Paper)({
  borderRadius: '16px',
  padding: '20px',
  background: 'white',
  border: '1px solid rgba(46, 90, 51, 0.08)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: '0 8px 24px rgba(46, 90, 51, 0.12)',
    borderColor: '#2e5a33',
  },
});

const ActivityCard = styled(Card)({
  borderRadius: '20px',
  background: 'white',
  border: '1px solid rgba(46, 90, 51, 0.08)',
});

interface DashboardStats {
  totalProductos: number;
  productosStockBajo: number;
  ventasHoy: number;
  totalClientes: number;
  alertasActivas: number;
  pedidosPendientes?: number;
  ingresosMes?: number;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [stats, setStats] = useState<DashboardStats>({
    totalProductos: 0,
    productosStockBajo: 0,
    ventasHoy: 0,
    totalClientes: 0,
    alertasActivas: 0,
    pedidosPendientes: 0,
    ingresosMes: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const data = await dashboardService.getStats();
      setStats({
        totalProductos: data.totalProductos || 0,
        productosStockBajo: data.productosStockBajo || 0,
        ventasHoy: data.ventasHoy || 0,
        totalClientes: data.totalClientes || 0,
        alertasActivas: data.alertasActivas || 0,
        pedidosPendientes: data.pedidosPendientes || 0,
        ingresosMes: data.ingresosMes || 0,
      });
      setError(null);
    } catch (err) {
      setError('Error al cargar los datos');
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const getDate = () => {
    return new Date().toLocaleDateString('es-CL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const StatCard: React.FC<{
    title: string;
    value: number | string;
    icon: React.ReactNode;
    color: string;
    trend?: number;
    subtitle?: string;
    format?: 'number' | 'currency';
  }> = ({ title, value, icon, color, trend, subtitle, format = 'number' }) => {
    const formattedValue = format === 'currency' 
      ? `$${Number(value).toLocaleString('es-CL')}`
      : Number(value).toLocaleString('es-CL');

    return (
      <StatsCard>
        <CardContent sx={{ pt: 4, px: 3, pb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <StatsIconWrapper bgcolor={color}>
              {icon}
            </StatsIconWrapper>
            {trend !== undefined && (
              <Chip
                size="small"
                icon={trend >= 0 ? <ArrowUpward sx={{ fontSize: 14 }} /> : <ArrowDownward sx={{ fontSize: 14 }} />}
                label={`${Math.abs(trend)}%`}
                sx={{
                  bgcolor: trend >= 0 ? 'success.light' : 'error.light',
                  color: trend >= 0 ? 'success.dark' : 'error.dark',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
            )}
          </Box>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 700, 
              color: '#1a1a1a',
              mt: 2,
              mb: 0.5,
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
            }}
          >
            {loading ? <Skeleton width={80} /> : formattedValue}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#666',
              fontWeight: 500,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </CardContent>
      </StatsCard>
    );
  };

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert 
          severity="error" 
          action={
            <IconButton color="inherit" size="small" onClick={fetchDashboardData}>
              <Refresh />
            </IconButton>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Welcome Card */}
      <WelcomeCard sx={{ mb: 4 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 }, position: 'relative', zIndex: 1 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}>
            <Box>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 1,
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                }}
              >
                {getGreeting()}, {user?.first_name || 'Administrador'} 👋
              </Typography>
              <Typography 
                sx={{ 
                  opacity: 0.9,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }}
              >
                {getDate()}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                <Chip 
                  label={`${stats.pedidosPendientes || 0} pedidos pendientes`}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    fontWeight: 500,
                  }}
                  icon={<LocalShipping sx={{ color: 'white !important' }} />}
                />
                {stats.alertasActivas > 0 && (
                  <Chip 
                    label={`${stats.alertasActivas} alertas`}
                    sx={{ 
                      bgcolor: 'rgba(255,193,7,0.3)', 
                      color: 'white',
                      fontWeight: 500,
                    }}
                    icon={<Warning sx={{ color: 'white !important' }} />}
                  />
                )}
              </Box>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <img 
                src="/assets/logos/logo-el-pinon-nuevo.png" 
                alt="El Piñón"
                style={{ 
                  width: '100px', 
                  height: '100px',
                  borderRadius: '20px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                  background: 'white',
                  padding: '8px',
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </WelcomeCard>

      {/* Stats Grid */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          lg: 'repeat(4, 1fr)',
        },
        gap: { xs: 3, md: 4 },
        mb: 4,
        pt: 4, // Space for floating icons
      }}>
        <StatCard
          title="Ventas de Hoy"
          value={stats.ventasHoy}
          icon={<AttachMoney sx={{ fontSize: 28 }} />}
          color="#2e5a33"
          trend={12}
          format="currency"
        />
        <StatCard
          title="Total Productos"
          value={stats.totalProductos}
          icon={<Inventory sx={{ fontSize: 28 }} />}
          color="#1976d2"
          subtitle={`${stats.productosStockBajo} con stock bajo`}
        />
        <StatCard
          title="Clientes Registrados"
          value={stats.totalClientes}
          icon={<People sx={{ fontSize: 28 }} />}
          color="#7b1fa2"
          trend={8}
        />
        <StatCard
          title="Pedidos Pendientes"
          value={stats.pedidosPendientes || 0}
          icon={<ShoppingCart sx={{ fontSize: 28 }} />}
          color="#ed6c02"
        />
      </Box>

      {/* Activity and Quick Actions */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
        gap: 3,
      }}>
        {/* Recent Activity */}
        <ActivityCard>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e5a33' }}>
                Actividad Reciente
              </Typography>
              <IconButton size="small">
                <MoreVert />
              </IconButton>
            </Box>
            
            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[1, 2, 3].map((i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="60%" />
                      <Skeleton width="40%" />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  { icon: <ShoppingCart />, color: '#2e5a33', text: 'Nuevo pedido #1234', time: 'Hace 5 min' },
                  { icon: <People />, color: '#1976d2', text: 'Cliente registrado', time: 'Hace 15 min' },
                  { icon: <Inventory />, color: '#ed6c02', text: 'Stock bajo: Manzanas', time: 'Hace 1 hora' },
                  { icon: <AttachMoney />, color: '#2e7d32', text: 'Pago recibido $25.000', time: 'Hace 2 horas' },
                ].map((item, index) => (
                  <Box 
                    key={index}
                    sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      alignItems: 'center',
                      p: 1.5,
                      borderRadius: 2,
                      transition: 'all 0.2s',
                      '&:hover': { bgcolor: 'rgba(46, 90, 51, 0.04)' },
                    }}
                  >
                    <Avatar sx={{ bgcolor: `${item.color}15`, color: item.color }}>
                      {item.icon}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.text}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.time}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>
        </ActivityCard>

        {/* Quick Actions */}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e5a33', mb: 2 }}>
            Acciones Rápidas
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            {[
              { icon: <ShoppingCart />, label: 'Ver Pedidos', color: '#2e5a33' },
              { icon: <Inventory />, label: 'Agregar Producto', color: '#1976d2' },
              { icon: <People />, label: 'Ver Clientes', color: '#7b1fa2' },
              { icon: <Warning />, label: 'Ver Alertas', color: '#ed6c02' },
            ].map((action, index) => (
              <QuickActionCard key={index} elevation={0}>
                <Avatar 
                  sx={{ 
                    width: 50, 
                    height: 50, 
                    bgcolor: `${action.color}15`,
                    color: action.color,
                  }}
                >
                  {action.icon}
                </Avatar>
                <Typography 
                  variant="body2" 
                  sx={{ fontWeight: 600, color: '#333', textAlign: 'center' }}
                >
                  {action.label}
                </Typography>
              </QuickActionCard>
            ))}
          </Box>

          {/* Stock Alert */}
          {stats.productosStockBajo > 0 && (
            <Paper 
              sx={{ 
                mt: 3, 
                p: 2, 
                borderRadius: 3,
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                border: '1px solid #ffb74d',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: '#ed6c02' }}>
                  <Warning />
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#e65100' }}>
                    Alerta de Stock
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stats.productosStockBajo} productos con stock bajo
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
